<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <b-alert :show="isLocked" variant="danger" class="font-weight-bold text-center">
            No Edits Allowed - Already Submitted
        </b-alert>

        <div v-if="!replyLoaded" key="replyStillLoading">
            <busy-spinner inner-class="my-8"></busy-spinner>
        </div>  

        <div v-else key="replyIsLoaded">  <!-- must hide form until existing data is loaded and we set data in form -->
            <!-- <b-overlay :show="loading" no-wrap></b-overlay> -->
            <div>  
                <div
                    class="d-flex align-items-center"
                >
                    <div class="w-25">
                        <p class="spl-attribute-labels w-25 mb-0">File Upload</p>
                    </div>
                    <div class="flex-fill">
                        <b-skeleton-wrapper :loading="loading">
                            <template #loading>
                                <b-skeleton width="65%" height="30px"></b-skeleton>
                            </template>
                            <h4 class="mb-0">{{ docRequestData ? docRequestData.title : '' }}</h4>
                        </b-skeleton-wrapper>
                    </div>
                </div>
                <hr>
                <div
                    v-if="docRequestData && docRequestData.instructions"
                >
                    <div class="d-flex align-items-center">
                        <p class="spl-attribute-labels w-25 mb-0">Instructions</p>
                        <div class="flex-fill">
                            <b-skeleton-wrapper :loading="loading">
                                <template #loading>
                                    <b-skeleton width="80%" height="16px"></b-skeleton>
                                    <b-skeleton width="60%" height="16px"></b-skeleton>
                                </template>
                                <p class="mb-0">{{ docRequestData.instructions }}</p>
                            </b-skeleton-wrapper>
                        </div>
                    </div>
                    <hr>
                </div>
                
                <div
                    v-if="docRequestData && docRequestData.template_file"
                >
                    <div
                        class="d-flex align-items-center"
                    >
                        <p class="spl-attribute-labels w-25 mb-0">Template File</p>
                        <div class="flex-fill">
                            <b-skeleton-wrapper :loading="loading">
                                <template #loading>
                                    <b-skeleton type="button"></b-skeleton>
                                </template>
                                <file-download-badge
                                    v-if="docRequestData && docRequestData.template_file"
                                    :file-data="docRequestData.template_file"
                                ></file-download-badge>
                            </b-skeleton-wrapper>
                        </div>
                    </div>
                    <hr>
                </div>
            </div>

            <div>
                <FormulateForm
                    v-model="replyFormData"
                    name="replyForm"
                    @submit="handleSubmit"
                >
                    <!-- <FormulateInput
                        id="docrequestreply-formulate-file-input"
                        ref="formulateFileInput"
                        type="file"
                        name="upload"
                        label="Your Completed File"
                        validation="required"
                        validation-name="Completed File"
                        :disabled="isLocked"
                        :file-remove-class="isLocked ? 'd-none' : []"
                        :upload-area-mask-class="['bg-gray-100']"
                        :outer-class="['formulate-horizontal']"
                        :uploader="formulateUploader"
                        upload-behavior="live"
                    /> -->
                    <FormulateInput
                        id="docrequestreply-formulate-file-input"
                        ref="formulateFileInput"
                        type="file"
                        name="files"
                        :multiple="allowMultipleFiles"
                        :add-label="allowMultipleFiles ? '+ Add File' : false"
                        :label="allowMultipleFiles ? 'Your Completed File(s)' : 'Your Completed File'"
                        validation="required"
                        :validation-name="allowMultipleFiles ? 'Completed File(s)' : 'Completed File'"
                        :disabled="isLocked"
                        :file-remove-class="isLocked ? 'd-none' : []"
                        :upload-area-mask-class="['bg-gray-100']"
                        :outer-class="['formulate-horizontal']"
                        :uploader="formulateUploader"
                        upload-behavior="live"
                    />  <!-- NOTE: we must set add-label prop to false to disable adding.... just changing the multiple prop doesn't seem to work -->
                    <FormulateInput
                        name="comment"
                        type="textarea"
                        label="Additional Comment?"
                        placeholder="Add any additional comments... (optional)"
                        rows="3"
                        help="Optional"
                        :disabled="isLocked"
                        :outer-class="['formulate-horizontal']"
                    />
                    <FormulateErrors />
                </FormulateForm>
                <div class="my-3 d-flex justify-content-center">
                    <b-button
                        variant="gray-secondary"
                        size="lg"
                        class="mr-3"
                        @click="triggerCancel"
                    >
                        <span>Cancel</span>
                    </b-button>
                    <b-button
                        variant="primary"
                        size="lg"
                        :disabled="saving || uploadPending || isLocked"
                        @click="triggerSubmit"
                    >
                        <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                        <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                        <span>{{ saving ? 'Saving' : 'Save' }}</span>
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

export default {
    name: 'DocRequestReply',
    components: {},
    props: {  // pass in either the reply ID (will get Obj from API) or full reply Object (will not make API call)
        replyId: {
            type: [String, Number],
            required: false,
        },
        replyObj: {
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            // formulate form errors
            loading: false,
            saving: false,
            replyLoaded: false,  // set this to true when you know you have replyFormData, or else formulate form does not update!
            uploadPending: false,
            showSuccess: false,
            fileCategory: 'docrequest_uploads',
            docRequestData: {},  // data of parent docRequest, passed in via prop also and set in created hook
            replyFormData: {},  // v-model with the form - use setFormData() method
            localReplyId: '',  // set this in created hook, depending if you passed in Obj or ID
            isLocked: false,  // set true if already submitted, use to disable inputs and submit button
        }
    },
    computed: {
        allowMultipleFiles() {
            // we get this from the docrequest data
            const allow = this.docRequestData && this.docRequestData.allow_multiple_reply_files;
            console.log('allowMultipleFiles computed to:', allow);
            return allow;
        },
    },
    created: function() {
        // until you figure out which way is best...
        // if you passed in replyId, use that and call this.getReply()
        // otherwise, use replyObj
        if (this.replyId) {
            this.localReplyId = this.replyId;
            this.getReply();
        } else if (this.replyObj && !isEmpty(this.replyObj)) {  // if you passed in Object instead.... 
            this.localReplyId = this.replyObj.id;
            this.isLocked = this.replyObj.is_submitted;
            this.setFormData(this.replyObj);
            this.docRequestData = this.replyObj.docrequest_full;
            this.replyLoaded = true;
        } else {
            console.error('Must pass in either replyId or replyObj')
        }
    },
    mounted: function() {
        // if we passed in replyObj, emit 'loaded' event here because parent will expect it -- used for parent to reveal this
        // otherwise, we'll emit it at the end of getReply()
        // we'll check this by looking at replyData object... that's the most direct way to know if we have it
        if (this.replyFormData && this.replyFormData.id) {
            this.$emit('loaded');
        }
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        getReply: function() {
            this.loading = true;
            return suppliosApi.docRequestReply.retrieve(this.localReplyId)
                .then(response => {
                    console.log('Got docRequestReply data:');
                    console.log(response.data);
                    this.isLocked = response.data.is_submitted;
                    this.setFormData(response.data);  // set local data the same way you do in created hook if passed in directly
                    this.docRequestData = response.data.docrequest_full;
                    this.$emit('loaded');  // tell parent that we're fully loaded... so it can reveal us if that's what it was doing
                    this.replyLoaded = true;
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while retrieving your response');
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                })
        },
        setFormData: function(apiData) {
            // set the local formulate v-model object with data from API or prop data object, particularly to make the file input work
            let localData = pick(apiData, ['id', 'comment']);
            // then take 'upload' object, get the 'plain_url' and make an array of objects with 'url' key, that's what vue-formulate file input expects
            // OLD WAY
            // if (apiData.upload && apiData.upload.plain_url) {
            //     localData['upload'] = [{url: apiData.upload.plain_url}];
            // }
            // NEW WAY
            if (Array.isArray(apiData.active_files) && apiData.active_files.length) {
                // locally, we use 'files' -- becaue that's what we'll send to the API
                // you might want to check for 'active_files' OR 'files' in whatever gets passed in here...?
                localData['files'] = apiData.active_files.map(file => {
                    return {id: file.id, url: file.plain_url};
                });
            }
            this.replyFormData = Object.assign({}, localData);
        },
        formulateUploader: function(file, progress, error, options) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = this.fileCategory;
            let parentId = this.localReplyId;  // the ID of the 'parent' for this file - optional
            let title = '';  // you could pass title to asyncSplFileSlinger if you know it in advance
            let uploadResponse = asyncSplFileSlinger(file_category, file, progress, error, options, title, parentId);
            let finalReturn = uploadResponse.then(resp => {
                this.uploadPending = false;
                console.log('Dumping resp from our file uploader:');
                console.log(resp);
                return resp;
            });
            return finalReturn;
        },
        triggerSubmit: function() {
            this.$formulate.submit('replyForm');
        },
        triggerCancel: function() {
            this.$emit('cancel');
        },
        formatDataForSave: function(data) {
            // consistent way of prepping things to send to API
            let pickedData = pick(data, ['id', 'comment']);
            // OLD WAY
            // if (data.upload && data.upload.length && data.upload[0].id) {  // data.upload is an array of objects {id, url} --> we want to grab the ID -- that's the ID we have for this file in our DB
            //     // 'upload' should be the file ID -- only add it to the post data if you have the id... which you won't have if it was an existing file (due to how vue-formulate works)
            //     // if you don't have upload id, it won't even be in the post data!
            //     // this is all because of a bug in vue-formulate 2.4.5... had to regress to 2.4.4 to make it work
            //     pickedData['upload'] = data.upload[0].id;  // vue formulate always makes an array of files, you have to pull out first one, and grab the id
            // }
            // NEW WAY - input is 'files' now -- and that's what our API expects, but just the IDs (or 'active_files'?)
            pickedData['files'] = data.files.map(file => {
                return file.id;
            });
            return pickedData;
        },
        handleSubmit: function(formData) {
            console.log('************\nFrom handleSubmit, here is formData we got:');
            console.log(formData);
            let dataToSave = this.formatDataForSave(formData);
            this.saving = true;
            return suppliosApi.docRequestReply.partialUpdate(this.localReplyId, dataToSave)
                .then(response => {
                    //this.setLocalData(response.data);
                    console.log(response.data);
                    this.$emit('saved');  // just emit this... save overlay is part of the parent
                    /* this.showSuccess = true;
                    setTimeout(() => {
                        this.$emit('saved');
                    }, 2000); */
                    return true;
                })
                .catch(error => {
                    // try to get errors from API and set them in the form
                    if (error.response && error.response.data) {
                        const { non_field_errors, ...fieldErrors } = error.response.data;
                        this.$formulate.handle({
                            formErrors: non_field_errors || [],
                            inputErrors: fieldErrors || {},
                        }, 'replyForm');
                    } else {
                        this.handleError(error, 'Error while updating data');
                    }
                    return false;
                })
                .then(success => {
                    this.saving = false;
                    return success;
                });
        }
    }
}
</script>

<style>

</style>